// @ts-nocheck
import React, { useEffect, useMemo, useRef, useState } from "react";
import "../styles/SharedSelect.css";

type SharedSelectProps = {
  className?: string;
  children?: React.ReactNode;
  value?: string | number;
  onChange?: (event: any) => void;
  disabled?: boolean;
  id?: string;
  name?: string;
  required?: boolean;
  placeholder?: string;
  "aria-label"?: string;
};

export default function SharedSelect({ className, children, ...props }: SharedSelectProps) {
  const {
    value,
    onChange,
    disabled,
    id,
    name,
    required,
    "aria-label": ariaLabel,
    placeholder,
  } = props;
  const [isOpen, setIsOpen] = useState(false);
  const rootRef = useRef(null);

  const optionList = useMemo(
    () =>
      React.Children.toArray(children)
        .filter((node) => React.isValidElement(node) && node.type === "option")
        .map((node) => ({
          value: node.props.value ?? node.props.children,
          label: node.props.children,
          disabled: Boolean(node.props.disabled),
        })),
    [children]
  );

  const current = optionList.find((item) => String(item.value) === String(value));
  const displayLabel = current?.label ?? placeholder ?? "Select";

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, []);

  const mergedClassName = ["shared-select", className].filter(Boolean).join(" ");

  const handlePick = (nextValue) => {
    if (disabled) return;
    onChange?.({
      target: {
        value: String(nextValue ?? ""),
        name: name || "",
      },
      currentTarget: {
        value: String(nextValue ?? ""),
        name: name || "",
      },
    });
    setIsOpen(false);
  };

  return (
    <div className="shared-select-root" ref={rootRef}>
      <button
        type="button"
        id={id}
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        className={mergedClassName}
        onClick={() => !disabled && setIsOpen((prev) => !prev)}
        disabled={disabled}
      >
        <span className="shared-select-label">{displayLabel}</span>
      </button>

      {required && <input type="hidden" name={name} value={value ?? ""} />}

      {isOpen && (
        <div className="shared-select-menu" role="listbox">
          {optionList.map((item) => {
            const selected = String(item.value) === String(value);
            return (
              <button
                key={String(item.value)}
                type="button"
                className={`shared-select-option ${selected ? "selected" : ""}`}
                onClick={() => handlePick(item.value)}
                disabled={item.disabled}
                role="option"
                aria-selected={selected}
              >
                {item.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
